/* global ProfileEnabled */

import React from 'react';
import { withRouter } from 'react-router';
import _ from 'lodash';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon from 'components/Icon';
import { isMvpdSubscription, isStaticRender, urlIsExternal } from 'helpers/ContentHelper';
import { noop } from 'helpers/GlobalHelper';
import { clearCache } from 'helpers/auth.clientless';
import {
  Events,
  getSegmentSignOutStartSource,
  handleAnalytics,
  TrackEvents as GeneralTrackEvents,
} from '@foxcorp/delta-web-analytics';
import {
  helperGetNetworkUpsellUrl,
  helperGoToRelativeUrl,
  redirectToExternalUrl,
  setPathForReEntryRedirect,
} from 'helpers/RouteHelper';
import { setLocalStorageItem } from 'helpers/Storage';
import nationStyle from './Nation.BuythroughProfileIcon.module.scss';

const style = nationStyle;

const disableAuthentication = () => {
  const { disableAuthentication = false } = window.ExternalConfig;

  return disableAuthentication;
};

const ProfileOption = withRouter(props => {
  const { favoritesManager, isFreemium, location, params, option } = props;
  const hasFavorites = _.get(favoritesManager, 'favoritesDetails', []).length > 0;
  if (option.id === 'manageFavorites' && !hasFavorites) return null;
  const pathName = _.get(location, 'pathname');
  let { url } = option;
  if (option.id === 'account' && isFreemium) url = `${url}/?freemium=true`;
  const onClick = () => {
    if (option.id === 'signOut') {
      const source = getSegmentSignOutStartSource({
        networkUpsellPage: helperGetNetworkUpsellUrl(),
        pathName,
        params,
      });
      if (source) {
        setLocalStorageItem({
          key: GeneralTrackEvents.FXPL_PROFILE_SIGN_OUT_SOURCE,
          value: source,
        });
      }
      handleAnalytics({ event: Events.PROFILE_LOGGED_OUT });
      clearCache();
    }
    handleAnalytics({
      event: Events.GLOBAL_NAV_CLICKED,
      payload: { page_item_title: option.label },
    });
    if (isFreemium) setPathForReEntryRedirect();
    if (urlIsExternal(url)) {
      redirectToExternalUrl(url);
    } else {
      helperGoToRelativeUrl(url);
    }
  };
  const button = (
    <button key={option.id} className={style.profileOption} onClick={onClick}>
      {option.label}
    </button>
  );

  return button;
});

const ProfileOptions = ({ account, favoritesManager, isFreemium, renderProfileDropdown }) => {
  const menuPath = 'profile.dropdownMenu';
  const options = _.get(window.ExternalConfig, menuPath, []);
  const profileOptionsClassNames = [
    style.profileOptionsContainer,
    renderProfileDropdown ? style.showProfileOptions : style.hideProfileOptions,
  ];
  if (!options.length) return null;
  const profileOptions = options.map(option => (
    <ProfileOption
      account={account}
      favoritesManager={favoritesManager}
      isFreemium={isFreemium}
      option={option}
      key={`dropdown-${option.id}`}
    />
  ));
  return (
    <div data-test="profile-options" className={classNames(profileOptionsClassNames)}>
      {profileOptions}
    </div>
  );
};

ProfileOptions.propTypes = {
  account: PropTypes.shape({}).isRequired,
  favoritesManager: PropTypes.shape({}).isRequired,
  isFreemium: PropTypes.bool,
  renderProfileDropdown: PropTypes.func.isRequired,
};

const BuythroughProfileIcon = ({
  account,
  favoritesManager,
  isAnonymous,
  isFreemium,
  isWeb,
  renderProfileDropdown,
  setWrapperRef,
  toggleProfileDropdown,
}) => {
  if (!ProfileEnabled || isStaticRender() || disableAuthentication()) return null;
  const userIcon = !isAnonymous || isMvpdSubscription() ? style.userIconAccount : style.userIcon;
  const profileDropdownContainerClasses = [
    style.profileDropdownContainer,
    style.rightIcon,
    style.accountDropdown,
  ];
  const profileIconOnClick = isWeb() ? noop : toggleProfileDropdown;
  if (renderProfileDropdown) profileDropdownContainerClasses.push(style.profileDropdownOpen);
  const ariaLabel = renderProfileDropdown ? 'close profile dropdown' : 'open profile dropdown';
  const icon = <Icon icon="user-unfilled" ariaHidden />;
  const displayName = icon;
  const arrowIcon = (
    <Icon
      color="white"
      icon={renderProfileDropdown ? 'keyboard-arrow-up' : 'keyboard-arrow-down'}
      ariaHidden
    />
  );

  return (
    <div
      className={classNames(profileDropdownContainerClasses)}
      onClick={profileIconOnClick}
      ref={setWrapperRef}
    >
      <div
        data-test="profile-dropdown"
        role="button"
        aria-label={ariaLabel}
        className={style.profileIconContainer}
        tabIndex="0"
      >
        <div data-test="profile-icon" className={userIcon}>
          {displayName}
        </div>
        {arrowIcon}
        {ProfileOptions({ account, favoritesManager, isFreemium, renderProfileDropdown })}
      </div>
    </div>
  );
};

BuythroughProfileIcon.propTypes = {
  account: PropTypes.shape({}).isRequired,
  favoritesManager: PropTypes.shape({}).isRequired,
  isAnonymous: PropTypes.bool.isRequired,
  isWeb: PropTypes.func.isRequired,
  isFreemium: PropTypes.bool,
  renderProfileDropdown: PropTypes.func.isRequired,
  setWrapperRef: PropTypes.func.isRequired,
  toggleProfileDropdown: PropTypes.func.isRequired,
};

export default BuythroughProfileIcon;
